/*
 * Reporting source readiness — the one place that decides, for any governed
 * analytics payload, WHICH source produced it and HOW complete that view is.
 *
 * SHERLOC and Pantheon read the same object. Before this module they each ran
 * their own algorithm over the same payload: Pantheon printed the source name
 * from `snapshotSourceLabel` while SHERLOC decided usability from the ingest
 * snapshot flags alone. A source that reports live over its own API — GA4 —
 * sets a label and none of those flags, so one card named the source and the
 * other, on the same render, said no usable source resolved. Two algorithms
 * over one payload is how a page contradicts itself; there is one here.
 *
 * The two questions are kept apart on purpose:
 *
 *   IDENTITY   — did a source declare itself, and can we name it? This is a
 *                function of the source declaration and nothing else. Absent
 *                tier, confidence or freshness NEVER erase an identity: those
 *                are governance metadata about a source, not the source.
 *
 *   READINESS  — how complete is what we know about it? A declared delivery
 *                basis (live / ingested) with its metadata is the complete
 *                case; anything short of that is PARTIAL, which states the
 *                gap rather than discarding the source.
 *
 * UNRESOLVED is reserved for its literal meaning: nothing declared a source
 * and nothing usable was reported. A surface rendering real metrics can never
 * reach it — that combination was the defect, not a state worth keeping.
 *
 * Twin-locked: byte-identical in the client and superadmin repos.
 */

export const REPORTING_SOURCE_STATES = Object.freeze({
  LIVE: "live",
  INGESTED: "ingested",
  PARTIAL: "partial",
  UNRESOLVED: "unresolved",
});

/*
 * Presentation lives with the state so the two portals cannot describe the
 * same governed state differently. Tones are the shell's existing three.
 */
export const REPORTING_SOURCE_STATE_PRESENTATION = Object.freeze({
  [REPORTING_SOURCE_STATES.LIVE]: Object.freeze({ label: "✓ Live", tone: "ok" }),
  [REPORTING_SOURCE_STATES.INGESTED]: Object.freeze({
    label: "⚠ Ingested fallback",
    tone: "warn",
  }),
  [REPORTING_SOURCE_STATES.PARTIAL]: Object.freeze({
    label: "◐ Partial — metadata incomplete",
    tone: "warn",
  }),
  [REPORTING_SOURCE_STATES.UNRESOLVED]: Object.freeze({
    label: "✕ Unresolved",
    tone: "error",
  }),
});

/* The governance fields a source may declare about itself. Optional, every
   one of them: their absence lowers readiness and is named, never hidden. */
export const GOVERNANCE_METADATA_LABELS = Object.freeze({
  delivery: "delivery basis",
  tier: "tier",
  confidence: "confidence",
  freshness: "freshness",
});

/* What the card prints when a source declares nothing for a field. The same
   words in all four slots — a bare dash reads as a rendering failure. */
export const NOT_DECLARED = "Not declared";

const textOrNull = (value) => {
  const text = String(value ?? "").trim();
  return text ? text : null;
};

const finiteOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

function minutesSince(isoStamp, nowMs) {
  const parsed = Date.parse(isoStamp || "");
  if (!Number.isFinite(parsed)) return null;
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  return Math.max(0, Math.round((now - parsed) / 60000));
}

/*
 * How the payload says it was obtained. Preserved exactly as the shell has
 * always read it, so no already-resolved paid-media surface changes state:
 * a live read stays live, an ingest snapshot stays ingested.
 */
function resolveDelivery(raw) {
  if (!raw) return null;
  if (raw.snapshotIsLive === true || raw.recoveredFromAutoFlightWindow === true) {
    return "live";
  }
  if (
    raw.snapshotIsFallback === true ||
    raw.snapshotUsedFallback === true ||
    raw.snapshotFound === true ||
    Boolean(textOrNull(raw.snapshotDataSource))
  ) {
    return "ingested";
  }
  return null;
}

function joinList(items) {
  if (items.length <= 1) return items.join("");
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * The canonical readiness object.
 *
 * @param {object|null} raw            the governed payload's `raw`
 * @param {boolean}     hasObservations whether the page resolved usable metrics
 * @param {number|null} nowMs          injected clock, for deterministic tests
 */
export function resolveReportingSourceReadiness({
  raw = null,
  hasObservations = false,
  nowMs = null,
} = {}) {
  const sourceLabel = textOrNull(raw?.snapshotSourceLabel);
  const delivery = resolveDelivery(raw);

  /*
   * Identity. A named source, or a payload asserting how it obtained the
   * numbers, is a resolved source. Deliberately NOT a function of tier,
   * confidence, freshness or spend-basis verification — those describe a
   * source that has already been identified.
   */
  const sourceResolved = Boolean(sourceLabel) || delivery != null;

  const tier = textOrNull(raw?.snapshotSourceTier);
  const confidence = finiteOrNull(raw?.snapshotSourceConfidence);
  const freshnessMinutes = minutesSince(raw?.snapshotSourceTimestamp, nowMs);

  const missingMetadata = [];
  if (delivery == null) missingMetadata.push("delivery");
  if (tier == null) missingMetadata.push("tier");
  if (confidence == null) missingMetadata.push("confidence");
  if (freshnessMinutes == null) missingMetadata.push("freshness");

  /*
   * The ladder.
   *
   * A declared delivery basis is what LIVE and INGESTED assert, so those two
   * are decided by the declaration alone — an absent tier or confidence is
   * named beside them rather than restating how the numbers were obtained.
   * That also means no already-resolved paid-media surface changes state.
   *
   * PARTIAL is the honest answer when a source is identified but did not say
   * how it delivered: real values, an incomplete governance declaration.
   *
   * UNRESOLVED requires BOTH no declared source and nothing usable — metrics
   * on the page are themselves evidence that something produced them, so a
   * page rendering numbers is at worst PARTIAL. That combination, not the
   * label, was the contradiction.
   */
  let state;
  if (delivery === "live") {
    state = REPORTING_SOURCE_STATES.LIVE;
  } else if (delivery === "ingested") {
    state = REPORTING_SOURCE_STATES.INGESTED;
  } else if (sourceResolved || hasObservations) {
    state = REPORTING_SOURCE_STATES.PARTIAL;
  } else {
    state = REPORTING_SOURCE_STATES.UNRESOLVED;
  }

  const presentation =
    REPORTING_SOURCE_STATE_PRESENTATION[state] ||
    REPORTING_SOURCE_STATE_PRESENTATION[REPORTING_SOURCE_STATES.UNRESOLVED];

  /*
   * The gap, in one sentence, only when there is one. It names the fields the
   * source did not declare — nothing is substituted for them anywhere.
   */
  const undeclared = missingMetadata
    .filter((key) => key !== "delivery")
    .map((key) => GOVERNANCE_METADATA_LABELS[key]);
  const undeclaredText = undeclared.length ? joinList(undeclared) : null;
  const metadataLimitation =
    sourceResolved && undeclaredText
      ? `${sourceLabel || "This reporting source"} declares no ${undeclaredText} for this ` +
        `window. The values are shown as the source reported them; nothing is substituted for the ` +
        `missing declaration.`
      : null;

  return {
    /* Identity */
    sourceResolved,
    sourceLabel,
    /* Readiness */
    state,
    statusLabel: presentation.label,
    statusTone: presentation.tone,
    delivery,
    hasObservations: Boolean(hasObservations),
    /* Governance metadata, exactly as declared */
    tier,
    confidence,
    freshnessMinutes,
    missingMetadata,
    undeclaredText,
    metadataLimitation,
  };
}

/**
 * SHERLOC's interpretation of the same object. One sentence, derived from the
 * canonical state — never from a second reading of the payload.
 */
export function describeReportingSourceForInterpretation(readiness) {
  const { state, sourceLabel, freshnessMinutes, undeclaredText } = readiness || {};

  if (state === REPORTING_SOURCE_STATES.LIVE) {
    return (
      `Metrics come from a live reporting source (${sourceLabel || "Live Feed"}); read them as ` +
      `current platform-reported values.`
    );
  }

  if (state === REPORTING_SOURCE_STATES.INGESTED) {
    return (
      `Metrics come from an ingested snapshot${
        freshnessMinutes != null ? ` about ${Math.round(freshnessMinutes / 60)}h old` : ""
      } — treat short-window movements with caution until a live source refreshes.`
    );
  }

  if (state === REPORTING_SOURCE_STATES.PARTIAL) {
    return sourceLabel
      ? `Metrics come from ${sourceLabel}, which reported usable values for this window.${
          undeclaredText
            ? ` It declares no ${undeclaredText}, so read them as reported — without a governed ` +
              `recency or confidence claim over them.`
            : ""
        }`
      : `Usable values are reported for this window, but no reporting source is declared for them. ` +
          `Read them as reported and confirm the source before deciding on them.`;
  }

  return `No usable reporting source resolved for this scope, so no performance interpretation is offered.`;
}

export default resolveReportingSourceReadiness;
